// THE FLOOR. There was none.
//
// Before this component, `grep -rn "ErrorBoundary\|componentDidCatch\|
// getDerivedStateFromError" src/` returned NOTHING across the whole frontend.
// React's documented behaviour when no boundary exists is to unmount the ENTIRE
// tree — so any unhandled throw anywhere below the router took out the sidebar,
// the header, the nav and the page together, and the operator got a blank white
// document with no way to navigate away and no text to report.
//
// That is why ~17 pages in the audit are recorded as "blank". They are almost
// certainly not 17 bugs; they are some smaller number of throws, rendered
// identically because a white screen erases whatever distinguished them.
//
// THIS IS A DIAGNOSTIC, NOT A COSMETIC. It exists to convert those anonymous
// blank screens into NAMED exceptions in a single deploy, without diagnosing
// any of them first: the route, the component and the message go on screen and
// into the console. Hiding the crash behind "Something went wrong" would keep
// the information loss that produced the audit finding — the whole value is in
// the name.
//
// Two placements, deliberately:
//   * around Layout's <Outlet/> — a page crash leaves the chrome intact and the
//     operator can click elsewhere. This is the one that matters day to day.
//   * around the router at the root — a throw in Layout itself is above the
//     first boundary, so without this one it is still a white screen.
import { Component, type ErrorInfo, type ReactNode } from "react";

import { AlertOctagonIcon, RefreshCwIcon } from "lucide-react";

interface Props {
  children: ReactNode;
  /** Where this boundary sits, for the log line and the on-screen text. */
  scope: string;
  /** Rendered above the details. Chrome-preserving boundaries pass a hint
   *  telling the operator the rest of the app still works. */
  hint?: string;
  /** Changing this value resets the boundary. The Outlet boundary passes the
   *  pathname so navigating away from a crashed route recovers without a
   *  reload — otherwise the first crash pins the error state forever and every
   *  subsequent nav click appears dead. */
  resetKey?: string;
}

interface State {
  error: Error | null;
  componentStack: string | null;
  /** Snapshot of `resetKey` when the error was caught. */
  caughtAt: string | undefined;
}

/** First frame of a React component stack: "    in AssetRegisterPage (at …)".
 *  That name is the single most useful token for an operator report, so it is
 *  extracted rather than left buried in a wall of frames. */
export function componentFromStack(stack: string | null | undefined): string | null {
  if (!stack) return null;
  for (const line of stack.split("\n")) {
    const m = line.match(/^\s*(?:in|at)\s+([A-Za-z0-9_$.]+)/);
    if (m && m[1]) return m[1];
  }
  return null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null, caughtAt: undefined };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // Navigating away from a crashed route clears the boundary. Without this
    // the operator keeps the chrome (good) but every link appears to do
    // nothing (worse than the crash).
    if (state.error && state.caughtAt !== undefined
        && props.resetKey !== state.caughtAt) {
      return { error: null, componentStack: null, caughtAt: undefined };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const component = componentFromStack(info.componentStack) ?? "unknown";
    const route = typeof window !== "undefined" ? window.location.pathname : "?";
    this.setState({
      componentStack: info.componentStack ?? null,
      caughtAt: this.props.resetKey,
    });
    // One line, greppable, with every field an operator or a log search needs.
    // eslint-disable-next-line no-console
    console.error(
      `[sview360] UI crash scope=${this.props.scope} route=${route} `
      + `component=${component} message=${error.message}`,
      error,
    );
  }

  render(): ReactNode {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    const component = componentFromStack(componentStack);
    const route = typeof window !== "undefined" ? window.location.pathname : "?";

    return (
      <div
        role="alert"
        data-testid="error-boundary"
        className="mx-auto my-8 max-w-2xl rounded-lg border border-destructive/40 bg-destructive/5 p-6"
      >
        <div className="flex items-start gap-3">
          <AlertOctagonIcon className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold">
              This screen failed to render
            </h2>
            {this.props.hint && (
              <p className="mt-1 text-sm text-muted-foreground">
                {this.props.hint}
              </p>
            )}

            {/* THE NAME. This block is the reason the component exists. */}
            <dl className="mt-4 space-y-1 text-xs">
              <div className="flex gap-2">
                <dt className="w-20 shrink-0 text-muted-foreground">Component</dt>
                <dd className="font-mono" data-testid="error-component">
                  {component ?? "unknown"}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-20 shrink-0 text-muted-foreground">Route</dt>
                <dd className="font-mono" data-testid="error-route">{route}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-20 shrink-0 text-muted-foreground">Error</dt>
                <dd
                  className="min-w-0 break-words font-mono text-destructive"
                  data-testid="error-message"
                >
                  {error.message || String(error)}
                </dd>
              </div>
            </dl>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center rounded-md border border-input bg-background px-3 py-1.5 text-xs hover:bg-muted"
              >
                <RefreshCwIcon className="mr-1 h-3 w-3" />
                Reload
              </button>
            </div>

            {componentStack && (
              <details className="mt-3">
                <summary className="cursor-pointer text-xs text-muted-foreground">
                  Component stack
                </summary>
                <pre className="mt-1 max-h-64 overflow-auto rounded bg-muted p-2 text-[10px] leading-relaxed">
                  {componentStack.trim()}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    );
  }
}
